import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { formatDateTime, formatKg } from '@/lib/format';

import { useReclassifiedReceipts } from './useReclassifiedReceipts';
import type { ReclassifiedReceiptDto, ReclassifiedReceiptStatus } from './types';

const NS = 'reclassified';
const P = 'efs.reclassified.receipts';

const statusVariant: Record<ReclassifiedReceiptStatus, 'secondary' | 'default'> = {
  draft: 'secondary',
  confirmed: 'default',
};

const StatusBadge = ({ status }: { status: ReclassifiedReceiptStatus }) => {
  const { t } = useTranslation(NS);
  return (
    <Badge variant={statusVariant[status]}>
      {t(`${P}.status.${status}`, { defaultValue: status })}
    </Badge>
  );
};

export const ReclassifiedReceiptsPage = () => {
  const { t } = useTranslation(NS);
  // The receipt list is scoped to a disassembly batch. The route param is the
  // disassembly_batch_id (see reclassified_receipts/routes.rs — `:id`).
  const { batchId } = useParams<{ batchId: string }>();

  const query = useReclassifiedReceipts(batchId);

  const rows = query.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t(`${P}.title`, { defaultValue: '再分類受入' })}
        subtitle={t(`${P}.subtitle`, {
          defaultValue: '旧再分類受入の履歴を参照します',
        })}
      />

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>
          {t(`${P}.messages.legacy_read_only_title`, {
            defaultValue: '履歴専用の画面です',
          })}
        </AlertTitle>
        <AlertDescription>
          {t(`${P}.messages.legacy_read_only`, {
            defaultValue:
              '新しい二次解体・混入品の処理は、解体画面の中間素材から次工程を指定してください。この画面では旧データの確認のみ行えます。',
          })}
        </AlertDescription>
      </Alert>

      {query.error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {t(`${P}.error.title`, { defaultValue: '読み込みできませんでした' })}
          </AlertTitle>
          <AlertDescription>{query.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <div className="flex items-center gap-2 py-12 text-sm text-efs-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('efs.common.actions.loading', { ns: 'common', defaultValue: '読み込み中…' })}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-efs-border py-12 text-center text-sm text-efs-text-muted">
          {t(`${P}.empty`, { defaultValue: 'まだ再分類受入がありません。' })}
        </div>
      ) : (
        <div className="rounded-md border border-efs-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t(`${P}.fields.source_inbound_package`, { defaultValue: '元の入庫ユニット' })}
                </TableHead>
                <TableHead className="text-right">
                  {t(`${P}.fields.weight`, { defaultValue: '重量 (kg)' })}
                </TableHead>
                <TableHead>{t(`${P}.fields.reason`, { defaultValue: '理由' })}</TableHead>
                <TableHead>
                  {t(`${P}.fields.new_inbound_package`, { defaultValue: '新しい入庫ユニット' })}
                </TableHead>
                <TableHead>{t(`${P}.fields.status`, { defaultValue: '状態' })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <ReceiptRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

interface ReceiptRowProps {
  row: ReclassifiedReceiptDto;
}

const ReceiptRow = ({ row }: ReceiptRowProps) => {
  const { t } = useTranslation(NS);
  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        {row.source_inbound_package_id.slice(0, 8)}…
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums">{formatKg(row.weight)}</TableCell>
      <TableCell className="max-w-[16rem] truncate text-sm text-efs-text-muted">
        {row.reason ?? '-'}
      </TableCell>
      <TableCell>
        {row.new_inbound_package_id ? (
          <div className="space-y-0.5">
            <span className="font-mono text-xs">{row.new_inbound_package_id.slice(0, 8)}…</span>
            <div className="text-[0.6875rem] text-efs-text-muted">
              {t(`${P}.messages.reentered`, {
                defaultValue: '新規在庫として再入庫（最終素材ではありません）',
              })}
            </div>
          </div>
        ) : (
          <span className="text-efs-text-muted">-</span>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge status={row.status} />
        {row.confirmed_at ? (
          <div className="mt-0.5 text-[0.6875rem] text-efs-text-muted">
            {formatDateTime(row.confirmed_at)}
          </div>
        ) : null}
      </TableCell>
    </TableRow>
  );
};

export default ReclassifiedReceiptsPage;
